// 리포트 메뉴 — 기능 스위치·샘플 메타·최근 목록.
// REPORT_ENABLED 를 false 로 내리면 사이드바 링크와 /report 라우트가 함께 사라진다
// (기능을 뺄 때 이 한 줄만 바꾸면 된다 — 기존 화면은 아무것도 건드리지 않는 구조).
import { useAuth } from "../stores/auth";
export const REPORT_ENABLED = true;

export type ReportKind = "corp" | "industry" | "theme";

/** 유형 고정 컬러 — 문서(표지·미리보기)의 색. 메뉴 UI 자체는 앱 토큰(--navy)을 따른다 */
export const KIND_LABEL: Record<ReportKind, string> = {
  corp: "기업", industry: "산업", theme: "테마",
};
export const KIND_COLOR: Record<ReportKind, string> = {
  corp: "#0C74EE", industry: "#0E8C6B", theme: "#6C4CE0",
};
export const KIND_DEEP: Record<ReportKind, string> = {
  corp: "#0A2E66", industry: "#07352A", theme: "#251C56",
};

export interface ReportSample {
  key: string;
  kind: ReportKind;
  title: string;          // 문서 제목(표지 헤드라인)
  subject: string;        // 대상(기업명·산업명·테마명)
  subjectSub: string;     // 시장·범위 등 한 줄
  heroLine: string;       // 추천 카드에 쓰는 결론 한 줄
  pages: number;
  asof: string;
  toc: [string, string][]; // [장 제목, 페이지]
  symbol?: string;         // 기업 샘플이면 종목 심볼 (검색 매칭용)
}

export const SAMPLES: ReportSample[] = [
  // 기업 샘플 2종은 실제 생성 파이프라인(report2) 산출물 — 파이프라인이 바뀌면
  // dev 에서 재생성해 자산(webp·pdf)과 이 메타를 함께 갱신한다 (2026-08-24 갱신).
  {
    key: "krafton", kind: "corp", symbol: "KRX:259960",
    title: "크래프톤, 성장과 수익성의 재조정",
    subject: "크래프톤", subjectSub: "코스피 · KRX:259960",
    heroLine: "매출 +22.8% 성장 — 영업이익률은 5개년 저점 31.7%",
    pages: 10, asof: "2026-08-29",
    toc: [
      ["Ⅰ. 실적", "p.3"], ["Ⅱ. 재무 안정성", "p.4"], ["Ⅲ. 동종업계 비교", "p.5"],
      ["Ⅳ. 시장 밸류에이션", "p.6"], ["평가 트리거 · 리스크", "p.7"], ["최근 IR — 회사 발표", "p.8"],
      ["최근 공시·뉴스·리서치", "p.9"], ["데이터 커버리지 · 고지", "p.10"],
    ],
  },
  {
    key: "reno", kind: "corp", symbol: "KRX:058470",
    title: "리노공업, 고수익 성장의 재개",
    subject: "리노공업", subjectSub: "코스닥 · KRX:058470",
    heroLine: "영업이익률 47.5% — 반도체 부품에서 드문 수익 구조",
    pages: 8, asof: "2026-08-24",
    toc: [
      ["Ⅰ. 실적", "p.3"], ["Ⅱ. 재무 안정성", "p.4"], ["Ⅲ. 동종업계 비교", "p.5"],
      ["Ⅳ. 시장 밸류에이션", "p.6"], ["최근 공시·뉴스·리서치", "p.7"], ["데이터 커버리지 · 고지", "p.8"],
    ],
  },
  {
    key: "ecom", kind: "industry",
    title: "국내 이커머스, 쿠팡과 나머지로 나뉜 시장",
    subject: "국내 이커머스", subjectSub: "KSIC G479 · 외감 28개사",
    heroLine: "외감 28개사 합산 매출의 71.6%가 쿠팡 한 곳",
    pages: 6, asof: "2026-08-21",
    toc: [
      ["Ⅰ. 시장 구조", "p.2"], ["Ⅱ. 수익성 지도", "p.3"], ["Ⅲ. 손익 전환", "p.4"],
      ["Ⅳ. 확인 포인트", "p.5"], ["부록. 전체 명단 · 데이터 범위", "p.6"],
    ],
  },
  {
    key: "club", kind: "theme",
    title: "영업이익률 30% 클럽, 상장 사업회사 21곳",
    subject: "영업이익률 30% 클럽", subjectSub: "코스피·코스닥 전수 스크리닝",
    heroLine: "상장 사업회사 21곳 — 바이오·반도체·게임이 4분의 3",
    pages: 6, asof: "2026-08-21",
    toc: [
      ["Ⅰ. 클럽 명단", "p.2"], ["Ⅱ. 업종 지도", "p.3"], ["Ⅲ. 이익률의 성격", "p.4"],
      ["Ⅳ. 시사점 · 확인 포인트", "p.5"], ["부록. 기준·제외 명단·데이터 범위", "p.6"],
    ],
  },
];

// 페이지 미리보기 이미지·PDF — 빌드에 포함되는 정적 자산 (dev 검증용 샘플 3종)
const pageImgs = import.meta.glob("../assets/report/*.webp", { eager: true, import: "default" }) as Record<string, string>;
const pdfs = import.meta.glob("../assets/report/*.pdf", { eager: true, import: "default" }) as Record<string, string>;

export function samplePages(key: string, n: number): string[] {
  const out: string[] = [];
  for (let i = 1; i <= n; i++) {
    const hit = Object.entries(pageImgs).find(([p]) => p.endsWith(`/${key}-${i}.webp`));
    if (hit) out.push(hit[1]);
  }
  return out;
}
export function samplePdf(key: string): string | undefined {
  const hit = Object.entries(pdfs).find(([p]) => p.endsWith(`/${key}.pdf`));
  return hit?.[1];
}

// ── 최근 만든 리포트 — 버핏 최근 목록과 같은 localStorage 패턴 ──────────────
export interface ReportRecent {
  key: string; kind: ReportKind; subject: string; created: string;
  symbol?: string; listed?: boolean;
  /** 생성물 S3 영구 URL — 있으면 '보기'로 이전 보고서를 다시 연다 */
  url?: string;
  /** 뷰어 복원용 메타 (url 과 함께 저장) */
  pages?: number; asof?: string; toc?: [string, string][];
}
// v1 은 계정 구분 없는 단일 키였다 — 같은 브라우저에서 계정을 바꾸면 남의
// 최근 목록이 그대로 보였다(2026-08-29 제보). buffettRecent·labFiles 와 같은
// 사용자별 키로 바꾸고, 옛 키는 누구 것인지 알 수 없으므로 이관 없이 지운다.
const RECENT_KEY_LEGACY = "va_report_recent_v1";

function recentKey(): string {
  // 사용자 구분 규칙은 labFiles·buffettRecent 와 동일 (uid||username||email 소문자)
  try {
    const u = useAuth.getState().user as Record<string, unknown> | null;
    const k = String(u?.uid || u?.username || u?.email || "_anon").toLowerCase();
    return `va_report_recent_v2::${k}`;
  } catch {
    return "va_report_recent_v2::_anon";
  }
}

export function reportRecentList(): ReportRecent[] {
  try {
    localStorage.removeItem(RECENT_KEY_LEGACY);
    return JSON.parse(localStorage.getItem(recentKey()) || "[]") as ReportRecent[];
  } catch {
    return [];
  }
}
export function recordReportRecent(r: Omit<ReportRecent, "created">): void {
  try {
    const list = reportRecentList().filter((x) =>
      r.symbol ? x.symbol !== r.symbol : x.key !== r.key);
    const next = [{ ...r, created: new Date().toISOString() }, ...list].slice(0, 12);
    localStorage.setItem(recentKey(), JSON.stringify(next));
  } catch {
    /* noop */
  }
}

// ── 뷰어 대화 요청 기록 — 서버 연결 전, 어떤 요청이 들어오는지 수집해 QA 에 쓴다 ──
const ASK_KEY = "va_report_asks_v1";
export function recordReportAsk(sampleKey: string, text: string): void {
  try {
    const list = JSON.parse(localStorage.getItem(ASK_KEY) || "[]") as unknown[];
    list.unshift({ sampleKey, text, at: new Date().toISOString() });
    localStorage.setItem(ASK_KEY, JSON.stringify(list.slice(0, 50)));
  } catch {
    /* noop */
  }
}
